import { isTesting } from 'js/src/libs/custom-utils';

const SLIDE_SETTLE_QUIET_MS = 200;
const SLIDE_SETTLE_TIMEOUT_MS = 2000;

let pendingSettleRaf: number | null = null;

type CenterSlideInfo = {
    center_slide_id: string;
    center_slide_index: number;
    center_sub_scene_index: number;
    center_slide_group_id: string;
};

class FocusAndScroll {
    /**
     * Control scrolling for the creator
     */
    static scrollToSlide(slideId: string, subsceneIndex = 0): void {
        const selector = `.slide-row[data-slideid="${slideId}"][data-subsceneindex="${subsceneIndex}"]`;
        if (pendingSettleRaf !== null) {
            cancelAnimationFrame(pendingSettleRaf);
            pendingSettleRaf = null;
        }
        const slide = document.querySelector(selector);
        if (slide) {
            FocusAndScroll.centerSlide(slide);
        }
        FocusAndScroll.centerOnceSlideListSettles(selector, slide);
    }

    private static slideListGeometry(slide: Element): string | null {
        const scrollParent = document.getElementById('long-form-creator-container');
        if (!scrollParent) {
            return null;
        }
        return `${scrollParent.scrollHeight}:${Math.round(slide.getBoundingClientRect().height)}`;
    }

    private static centerSlide(slide: Element): void {
        slide.scrollIntoView({
            behavior: isTesting() ? 'instant' : 'smooth',
            block: 'center',
            inline: 'nearest',
        });
    }

    /**
     * Callers routinely add a scene to luminary and scroll to it in the same breath, so React has
     * not mounted its row yet, and the rows that do mount resize their siblings a frame or more
     * later. Centering before the list settles leaves the target off-centre, and
     * updateFocusedSceneInfoOnScroll then hands focus to whichever row did end up centred,
     * discarding the focus the caller just set. So center again once the row exists and the list
     * has held the same geometry for SLIDE_SETTLE_QUIET_MS, giving up after SLIDE_SETTLE_TIMEOUT_MS.
     *
     * Seeded with the geometry at call time so a row that was already in place and never resizes
     * is not scrolled a second time, which would restart the smooth scroll from scrollToSlide and
     * override any scrolling the user started in the meantime. Only one loop runs at a time, so a
     * later request cannot be overridden by an earlier one whose row mounted late.
     */
    private static centerOnceSlideListSettles(selector: string, slideAtCallTime: Element | null) {
        const deadline = Date.now() + SLIDE_SETTLE_TIMEOUT_MS;
        let previousGeometry = slideAtCallTime
            ? FocusAndScroll.slideListGeometry(slideAtCallTime)
            : null;
        let geometryChanged = false;
        let unchangedSince = Date.now();

        const check = () => {
            pendingSettleRaf = null;
            const slide = document.querySelector(selector);
            const geometry = slide ? FocusAndScroll.slideListGeometry(slide) : null;
            const timedOut = Date.now() >= deadline;

            if (slide && geometry !== null) {
                if (geometry !== previousGeometry) {
                    previousGeometry = geometry;
                    unchangedSince = Date.now();
                    geometryChanged = true;
                }
                if (Date.now() - unchangedSince >= SLIDE_SETTLE_QUIET_MS || timedOut) {
                    if (geometryChanged) {
                        FocusAndScroll.centerSlide(slide);
                    }
                    return;
                }
            }

            if (!timedOut) {
                pendingSettleRaf = requestAnimationFrame(check);
            }
        };

        pendingSettleRaf = requestAnimationFrame(check);
    }

    /**
     * Calculates the current centermost scene
     */
    static calculateCentermostSceneInfo(): CenterSlideInfo | undefined {
        const scrollParent = document.getElementById('long-form-creator-container');
        if (!scrollParent) {
            return;
        }
        const scrollParentRect = scrollParent.getBoundingClientRect();
        const scrollParentMiddle = scrollParentRect.top + scrollParentRect.height / 2;

        let nearestSlide: HTMLDivElement | null = null;
        let nearestDistance = Infinity;
        const slides = document.querySelectorAll<HTMLDivElement>(
            '.slide-row.actually-contains-slide',
        );
        for (let i = 0; i < slides.length; i++) {
            const slide = slides[i];
            const slideRect = slide.getBoundingClientRect();
            const slideMiddle = slideRect.top + slideRect.height / 2;
            const distance = Math.abs(scrollParentMiddle - slideMiddle);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestSlide = slide;
            }
        }

        if (!nearestSlide) {
            return undefined;
        }
        const center_slide_group_id = nearestSlide.dataset.scenegroupid;
        const center_slide_id = nearestSlide.dataset.slideid;
        const center_slide_index = nearestSlide.dataset.sceneindex;
        const center_sub_scene_index = nearestSlide.dataset.subsceneindex;
        if (
            center_slide_group_id &&
            center_slide_id &&
            center_slide_index &&
            center_sub_scene_index
        ) {
            return {
                center_slide_group_id,
                center_slide_id,
                center_slide_index: parseInt(center_slide_index),
                center_sub_scene_index: parseInt(center_sub_scene_index),
            };
        }
        return undefined;
    }

    static getScrollParent(element: HTMLElement): HTMLElement | undefined {
        let parent = element.parentElement;

        while (parent) {
            const style = getComputedStyle(parent);
            const overflowY = style.overflowY;
            const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

            if (isScrollable && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }

            parent = parent.parentElement;
        }
    }
}

export default FocusAndScroll;
